import React, { useState } from 'react';
import { CheckCircle, AlertCircle, TrendingUp } from 'lucide-react';

interface BillingCode {
  code: string;
  type: 'ICD-10' | 'CPT';
  description: string;
  confidence: number;
  touchless: boolean;
}

export const BillingCodes: React.FC = () => {
  const [codes, setCodes] = useState<BillingCode[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedForReview, setSelectedForReview] = useState<string[]>([]);

  const handleGenerateCodes = async () => {
    setIsLoading(true);

    try {
      const response = await fetch('http://localhost:5000/api/ai/billing-codes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          encounter_data: {
            diagnoses: ['I10', 'E11.9', 'N18.3'],
            procedures: ['Extended exam', 'Labs'],
            visit_type: 'office'
          }
        })
      });

      const data = await response.json();
      if (data.success && data.codes) {
        setCodes(data.codes);
      }
    } catch (error) {
      console.error('Error generating codes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const touchlessCodes = codes?.filter(c => c.touchless) || [];
  const reviewCodes = codes?.filter(c => !c.touchless) || [];
  const avgConfidence = codes ? (codes.reduce((sum, c) => sum + c.confidence, 0) / codes.length * 100).toFixed(0) : 0;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Billing Automation (Phase 2)</h2>
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
          💰 Revenue Optimization
        </span>
      </div>

      {!codes ? (
        <div className="bg-green-50 p-8 rounded-lg border border-green-200 text-center">
          <TrendingUp className="mx-auto text-green-600 mb-3" size={32} />
          <h3 className="text-lg font-semibold text-green-900 mb-2">Auto-Suggest ICD-10 & CPT Codes</h3>
          <p className="text-green-700 mb-4">Generate billing codes from encounter notes. AI suggests high-confidence codes for instant submission.</p>
          <button
            onClick={handleGenerateCodes}
            disabled={isLoading}
            className="bg-green-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-400"
          >
            {isLoading ? '🤔 Generating...' : '→ Generate Codes for Sample Visit'}
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Success Metrics */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-2xl font-bold text-green-600">{touchlessCodes.length} of {codes.length}</p>
              <p className="text-sm text-green-700">High-confidence (touchless)</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-2xl font-bold text-blue-600">{avgConfidence}%</p>
              <p className="text-sm text-blue-700">Average confidence</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <p className="text-2xl font-bold text-purple-600">20%</p>
              <p className="text-sm text-purple-700">Est. revenue lift</p>
            </div>
          </div>

          {/* Touchless Codes */}
          {touchlessCodes.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="text-green-600" size={20} />
                <h3 className="font-semibold text-green-900">Touchless Codes ({touchlessCodes.length})</h3>
              </div>
              <p className="text-sm text-green-700 mb-3">These codes are high-confidence and ready for automatic submission:</p>
              <div className="space-y-2">
                {touchlessCodes.map((code) => (
                  <div key={code.code} className="bg-white p-3 rounded border border-green-200 flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-gray-900">{code.code}</p>
                      <p className="text-sm text-gray-600">{code.description}</p>
                    </div>
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                      {(code.confidence * 100).toFixed(0)}% confident
                    </span>
                  </div>
                ))}
              </div>
              <button className="mt-4 w-full bg-green-600 text-white py-2 rounded-lg font-semibold hover:bg-green-700">
                ✓ Auto-Submit {touchlessCodes.length} Codes
              </button>
            </div>
          )}

          {/* Review Codes */}
          {reviewCodes.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle className="text-yellow-600" size={20} />
                <h3 className="font-semibold text-yellow-900">Review Codes ({reviewCodes.length})</h3>
              </div>
              <p className="text-sm text-yellow-700 mb-3">Lower confidence codes - requires clinician review:</p>
              <div className="space-y-2">
                {reviewCodes.map((code) => (
                  <div key={code.code} className="bg-white p-3 rounded border border-yellow-200 flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{code.code}</p>
                      <p className="text-sm text-gray-600">{code.description}</p>
                    </div>
                    <div className="text-right">
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                        {(code.confidence * 100).toFixed(0)}%
                      </span>
                      <button
                        onClick={() => setSelectedForReview(prev =>
                          prev.includes(code.code) ? prev.filter(c => c !== code.code) : [...prev, code.code]
                        )}
                        className={`mt-2 text-xs px-2 py-1 rounded ${
                          selectedForReview.includes(code.code)
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-700'
                        }`}
                      >
                        {selectedForReview.includes(code.code) ? '✓ Approved' : 'Review'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-2">Billing Submission Summary</h3>
            <ul className="space-y-2 text-sm text-blue-800">
              <li>✓ {touchlessCodes.length} codes ready for automatic submission</li>
              <li>⏳ {reviewCodes.length} codes pending your review ({selectedForReview.length} approved)</li>
              <li>💰 Estimated processing time: 2 minutes (vs 20 min manual coding)</li>
              <li>📊 Estimated claim denial rate: 5% (industry avg 12%)</li>
            </ul>
            <button className="mt-4 w-full bg-blue-600 text-white py-2 rounded-lg font-semibold hover:bg-blue-700">
              Submit {touchlessCodes.length + selectedForReview.length} Codes
            </button>
          </div>

          {/* Reset Button */}
          <button
            onClick={() => setCodes(null)}
            className="w-full border border-gray-300 text-gray-700 py-2 rounded-lg font-semibold hover:bg-gray-50"
          >
            Generate New Codes
          </button>
        </div>
      )}

      {/* Key Metrics Footer */}
      <div className="grid grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-200">
        <div className="text-center">
          <p className="text-xl font-bold text-green-600">83%</p>
          <p className="text-xs text-gray-600">Touchless Rate</p>
        </div>
        <div className="text-center">
          <p className="text-xl font-bold text-blue-600">5m</p>
          <p className="text-xs text-gray-600">Processing Time</p>
        </div>
        <div className="text-center">
          <p className="text-xl font-bold text-purple-600">20%</p>
          <p className="text-xs text-gray-600">Revenue Lift</p>
        </div>
        <div className="text-center">
          <p className="text-xl font-bold text-orange-600">5%</p>
          <p className="text-xs text-gray-600">Denial Rate</p>
        </div>
      </div>
    </div>
  );
};
